/**
 * SurveyResults — admin view of aggregated survey answers for one tip.
 *
 * Reads GET /console/whats-new-tips/{id}/survey-results and renders per-question
 * distributions / means / NPS / free-text. Option labels come from the tip's
 * definition (the results endpoint returns values, not labels).
 *
 * See docs/FeatureDesigns/whats-new-surveys.md.
 */

import { useQuery } from '@tanstack/react-query';
import type { SurveyDefinition, SurveyResults as SurveyResultsData, SurveyQuestionResult } from '@shared-ui/whats-new';
import { fetchWithAdmin } from '../../api/fetchHelper';
import './SurveyResults.css';

interface SurveyResultsProps {
    tipId: string;
    definition: SurveyDefinition | null;
}

function Bar({ label, count, total }: { label: string; count: number; total: number }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="sr-bar-row">
            <span className="sr-bar-label" title={label}>{label}</span>
            <span className="sr-bar-track"><span className="sr-bar-fill" style={{ width: `${pct}%` }} /></span>
            <span className="sr-bar-count">{count} · {pct}%</span>
        </div>
    );
}

function QuestionResult({ q, def }: { q: SurveyQuestionResult; def: SurveyDefinition | null }) {
    const question = def?.questions.find(d => d.id === q.id);
    const labelFor = (value: string) => question?.options?.find(o => o.value === value)?.label ?? value;

    return (
        <div className="sr-q">
            <div className="sr-q-head">
                <span className="sr-q-prompt">{q.prompt}</span>
                <span className="sr-q-meta">{q.type} · {q.answered} answered</span>
            </div>

            {(q.type === 'single' || q.type === 'multi') && q.distribution && (
                <div className="sr-bars">
                    {Object.entries(q.distribution)
                        .sort((a, b) => b[1] - a[1])
                        .map(([value, count]) => (
                            <Bar key={value} label={labelFor(value)} count={count} total={q.answered} />
                        ))}
                </div>
            )}

            {(q.type === 'rating' || q.type === 'nps') && q.histogram && (
                <div className="sr-scale-result">
                    <div className="sr-bars">
                        {Object.entries(q.histogram)
                            .sort((a, b) => Number(a[0]) - Number(b[0]))
                            .map(([score, count]) => (
                                <Bar key={score} label={score} count={count} total={q.answered} />
                            ))}
                    </div>
                    <div className="sr-scale-summary">
                        {q.mean != null && <span>Mean <strong>{q.mean}</strong></span>}
                        {q.type === 'nps' && q.npsScore != null && (
                            <span>NPS <strong>{q.npsScore}</strong>
                                {q.npsBreakdown && <span className="sr-nps-bd"> ({q.npsBreakdown.promoters}▲ / {q.npsBreakdown.passives}· / {q.npsBreakdown.detractors}▼)</span>}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {q.type === 'text' && (
                q.texts && q.texts.length > 0
                    ? <ul className="sr-texts">{q.texts.map((t, i) => <li key={i}>{t}</li>)}</ul>
                    : <p className="sr-empty">No text responses.</p>
            )}
        </div>
    );
}

export function SurveyResults({ tipId, definition }: SurveyResultsProps) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['whats-new-survey-results', tipId],
        queryFn: () => fetchWithAdmin<SurveyResultsData>(`/console/whats-new-tips/${tipId}/survey-results`),
        staleTime: 60_000,
    });
    // Reach for the completion rate. Shares the effectiveness panel's query key,
    // so this is deduped, not a second round trip.
    const statsQ = useQuery({
        queryKey: ['whats-new-tip-stats', tipId],
        queryFn: () => fetchWithAdmin<{ peopleShown: number }>(`/console/whats-new-tips/${tipId}/stats`),
        staleTime: 60_000,
    });

    if (isLoading) return <p className="sr-hint">Loading results…</p>;
    if (error) return <p className="sr-hint">Couldn't load survey results.</p>;
    if (!data) return null;

    const peopleShown = statsQ.data?.peopleShown;
    const completion = peopleShown && peopleShown > 0
        ? Math.round((data.responseCount / peopleShown) * 100)
        : undefined;

    return (
        <div className="sr">
            <div className="sr-headline">
                <div><span className="sr-num">{data.responseCount}</span><span className="sr-lbl">responses</span></div>
                {completion != null && (
                    <div><span className="sr-num">{completion}%</span><span className="sr-lbl">of {peopleShown} shown</span></div>
                )}
            </div>
            {data.responseCount === 0 ? (
                <p className="sr-hint">No responses yet.</p>
            ) : (
                data.questions.map(q => <QuestionResult key={q.id} q={q} def={definition} />)
            )}
        </div>
    );
}
